import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { get } from 'lodash';

import { ProductModel } from '../modules/products';
import { ProductsService } from '../modules/products/products.service';
import { ProductTypes } from '../modules/products/product-types';
import { CompanyProfile } from 'projects/workspace/src/app/administration/models/company-profile.model';
import { VatDictionaryItemModel } from 'projects/workspace/src/app/accounting/accounting-settings-module/models';
import { VatDictionaryService } from 'projects/workspace/src/app/accounting/accounting-settings-module/services';

export const DEFAULT_SALES_PRICE_LIST_NAME = 'Default price list';

@Injectable({ providedIn: 'root' })
export class IncomingPositionVatRateService {
  private readonly legacyDefaultVatRate: number = 20;

  constructor(
    private readonly vatDictionaryService: VatDictionaryService,
    private readonly productsService: ProductsService,
  ) {}

  public resolve(
    product: ProductModel,
    companyProfile: CompanyProfile,
    vatBlocked: boolean,
    selectedProductType?: string,
  ): Observable<number> {
    if (vatBlocked) {
      return of(0);
    }

    const vatRateByDefault = get(
      product,
      'accountingSettings.vatRateByDefault',
      get(product, 'vatRateByDefault')
    );
    const glProductTypeId = get(
      product,
      'accountingSettings.glProductTypeId',
      get(product, 'glProductTypeId')
    );
    const isGeneralProduct = selectedProductType === ProductTypes.GENERAL
      || product.productType === ProductTypes.GENERAL
      || product.type === ProductTypes.GENERAL;

    if (isGeneralProduct) {
      if (vatRateByDefault !== null && vatRateByDefault !== undefined) {
        return of(+vatRateByDefault);
      }

      const productId = product.id || product.generalProductId;
      if (!productId) {
        return of(this.legacyDefaultVatRate);
      }

      return this.productsService.getGeneralProduct(productId)
        .pipe(
          map(response => this.getRateOrFallback(
            get(
              response,
              'item.accountingSettings.vatRateByDefault',
              get(response, 'item.vatRateByDefault', get(response, 'accountingSettings.vatRateByDefault'))
            )
          )),
          catchError(() => of(this.legacyDefaultVatRate))
        );
    }

    if (glProductTypeId) {
      return of(this.getRateOrFallback(vatRateByDefault));
    }

    if (!companyProfile || !companyProfile.isSmallBusiness) {
      return of(this.getRateOrFallback(get(product, 'prices.price.vat')));
    }

    const countryIso3 = get(companyProfile, 'legalAddress.country_iso3');
    if (!countryIso3) {
      return of(this.legacyDefaultVatRate);
    }

    return this.vatDictionaryService.getVatDictionary([countryIso3])
      .pipe(
        map((items: VatDictionaryItemModel[]) => {
          const primaryRate = (items || []).find((item: VatDictionaryItemModel) => item.primary);
          return this.getRateOrFallback(primaryRate && primaryRate.rate);
        }),
        catchError(() => of(this.legacyDefaultVatRate))
      );
  }

  private getRateOrFallback(rate: number): number {
    return rate === null || rate === undefined
      ? this.legacyDefaultVatRate
      : +rate;
  }
}
